// src/pages/super-admin/AdminManagement.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaUserShield,
  FaUsers,
  FaBuilding,
  FaCheck,
  FaTimes,
  FaSave,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaCalendar,
  FaUserCheck
} from 'react-icons/fa';

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({
    username: "",
    email: "",
    phoneNumber: "",
    address: "",
    companies: [],
    dateOfBirth: "",
    dateOfJoining: "",
    password: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(5);

  useEffect(() => {
    fetchAdmins();
    fetchCompanies();
  }, []);

  // ---------------------------------------------------------------------
  // Fetch All Admins (active only)
  // ---------------------------------------------------------------------
  const fetchAdmins = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admins`
      );
      // Returns only isActive = true from the backend
      setAdmins(response.data);
    } catch (error) {
      console.error("Error fetching admins:", error);
      Swal.fire("Error", "Failed to fetch admins", "error");
    }
  };

  // ---------------------------------------------------------------------
  // Fetch All Companies
  // ---------------------------------------------------------------------
  const fetchCompanies = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/companies`
      );
      setCompanies(response.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
      Swal.fire("Error", "Failed to fetch companies", "error");
    }
  };

  // ---------------------------------------------------------------------
  // Form Handlers
  // ---------------------------------------------------------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prevForm) => ({ ...prevForm, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    setForm((prevForm) => ({ ...prevForm, phoneNumber: e.target.value }));
  };

  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    const isChecked = e.target.checked;
    
    setForm((prevForm) => ({
      ...prevForm,
      companies: isChecked
        ? [...prevForm.companies, companyId]
        : prevForm.companies.filter(id => id !== companyId)
    }));
  };



  // ---------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------
  const validateForm = () => {
    const requiredFields = ["username", "email", "phoneNumber", "address"];
    for (let field of requiredFields) {
      if (!form[field]?.trim()) {
        Swal.fire("Warning", `${field} is required`, "warning");
        return false;
      }
    }
    if (form.companies.length === 0) {
      Swal.fire("Warning", "At least one company must be selected", "warning");
      return false;
    }
    // Email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      Swal.fire("Warning", "Invalid email format", "warning");
      return false;
    }
    // Phone
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(form.phoneNumber)) {
      Swal.fire("Warning", "Invalid phone number format", "warning");
      return false;
    }
    return true;
  };

  // ---------------------------------------------------------------------
  // (Optional) Check existing account
  // ---------------------------------------------------------------------
  const checkExistingAccount = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/check-account`,
        { email: form.email }
      );
      return response.data.exists;
    } catch (error) {
      console.error("Error checking existing account:", error);
      Swal.fire("Error", "Failed to check for existing account", "error");
      return false;
    }
  };

  // ---------------------------------------------------------------------
  // Add or Update
  // ---------------------------------------------------------------------
  const handleAddOrUpdateAdmin = async () => {
    if (!validateForm()) return;

    // Enforce the "account must exist" rule if desired
    if (!isEditing) {
      const exists = await checkExistingAccount();
      if (exists) {
        Swal.fire(
          "Warning",
          "Admin must not be associated with an existing account (any role)",
          "warning"
        );
        return;
      }
    }

    try {
      const payload = {
        username: form.username,
        email: form.email,
        phoneNumber: form.phoneNumber,
        address: form.address,
        companies: form.companies,
        dateOfBirth: form.dateOfBirth,
        dateOfJoining: form.dateOfJoining,
        password: form.password
      };

      if (isEditing) {
        // Update
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/admins/${editingId}`,
          payload
        );
        Swal.fire("Success", "Admin updated successfully", "success");
      } else {
        // Create
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/admins`, payload);
        Swal.fire("Success", "Admin added successfully", "success");
      }

      fetchAdmins();
      resetForm();
      closeFormModal();
    } catch (error) {
      console.error("Error saving admin:", error);
      Swal.fire("Error", "Failed to save admin", "error");
    }
  };

  // ---------------------------------------------------------------------
  // Start Editing
  // ---------------------------------------------------------------------
  const startEditing = async (admin) => {
    setIsEditing(true);
    setEditingId(admin._id);
    setForm({
      username: admin.username,
      email: admin.email,
      phoneNumber: admin.phoneNumber,
      address: admin.address,
      companies: admin.companies?.map(company => company._id) || [],
      dateOfBirth: admin.dateOfBirth
        ? new Date(admin.dateOfBirth).toISOString().split("T")[0]
        : "",
      dateOfJoining: admin.dateOfJoining
        ? new Date(admin.dateOfJoining).toISOString().split("T")[0]
        : "",
      password: "",
    });

    openFormModal();
  };

  // ---------------------------------------------------------------------
  // Deactivate
  // ---------------------------------------------------------------------
  const handleDeactivate = async (id) => {
    const confirmResult = await Swal.fire({
      title: "Confirm Deactivate",
      text: "Are you sure you want to deactivate this admin?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, deactivate",
      cancelButtonText: "Cancel",
    });

    if (confirmResult.isConfirmed) {
      try {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/admins/deactivate/${id}`
        );
        Swal.fire("Deactivated!", "Admin deactivated successfully.", "success");
        fetchAdmins();
      } catch (error) {
        console.error("Error deactivating admin:", error);
        Swal.fire("Error", "Failed to deactivate admin", "error");
      }
    }
  };

  // ---------------------------------------------------------------------
  // View Details
  // ---------------------------------------------------------------------
  const handleViewDetails = (id) => {
    fetchAdminById(id);
  };

  const fetchAdminById = async (id) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admins/${id}`
      );
      setSelectedAdmin(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error("Error fetching admin:", error);
      Swal.fire("Error", "Failed to fetch admin details", "error");
    }
  };

  // ---------------------------------------------------------------------
  // Reset Form
  // ---------------------------------------------------------------------
  const resetForm = () => {
    setForm({
      username: "",
      email: "",
      phoneNumber: "",
      address: "",
      companies: [],
      dateOfBirth: "",
      dateOfJoining: "",
      password: "",
    });
    setIsEditing(false);
    setEditingId(null);
  };

  // ---------------------------------------------------------------------
  // Modal Control
  // ---------------------------------------------------------------------
  const openFormModal = () => setShowFormModal(true);
  const closeFormModal = () => {
    setShowFormModal(false);
    resetForm();
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedAdmin(null);
  };

  // ---------------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------------
  const totalRecords = admins.length;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedAdmins = admins.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center">
              <FaUserShield className="text-blue-600 mr-4 text-2xl" />
              Admin Management
            </h1>
            <p className="text-gray-600 mt-2 text-sm">Manage system administrators and their permissions</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              openFormModal();
            }}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl hover:from-blue-700 hover:to-purple-700 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-sm"
          >
            <FaPlus className="w-5 h-5" />
            <span className="font-semibold">Add New Admin</span>
          </button>
        </div>
      </div>

      {/* Table of Active Admins */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
                >
                  <div className="flex items-center space-x-2">
                    <FaUserShield className="w-3 h-3" />
                    <span>Admin Details</span>
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
                >
                  <div className="flex items-center space-x-2">
                    <FaEnvelope className="w-3 h-3" />
                    <span>Contact</span>
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
                >
                  <div className="flex items-center space-x-2">
                    <FaBuilding className="w-3 h-3" />
                    <span>Companies</span>
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
                >
                  <div className="flex items-center space-x-2">
                    <FaCheck className="w-3 h-3" />
                    <span>Status</span>
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {paginatedAdmins.map((admin, index) => (
              <tr key={admin._id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200 group">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm">
                      {admin.username?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                        {admin.username}
                      </div>
                      <div className="text-xs text-gray-500">Admin #{index + 1}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2 text-xs">
                      <FaEnvelope className="w-3 h-3 text-gray-400" />
                      <span className="text-gray-900">{admin.email}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs">
                      <FaPhone className="w-3 h-3 text-gray-400" />
                      <span className="text-gray-600">{admin.phoneNumber}</span>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
                      {admin.companies && admin.companies.length > 0 
                        ? `${admin.companies.length} Companies`
                        : "No Companies"}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex items-center space-x-1 text-xs font-bold rounded-full shadow-md ${
                    admin.isActive 
                      ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800' 
                      : 'bg-gradient-to-r from-red-100 to-red-200 text-red-800'
                  }`}>
                    {admin.isActive ? <FaCheck className="w-2 h-2" /> : <FaTimes className="w-2 h-2" />}
                    <span>{admin.isActive ? 'Active' : 'Inactive'}</span>
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-xs font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleViewDetails(admin._id)}
                      className="bg-blue-100 text-blue-600 hover:bg-blue-200 p-1 rounded-lg transition-all duration-200 hover:scale-110"
                      title="View Details"
                    >
                      <FaEye className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => startEditing(admin)}
                      className="bg-green-100 text-green-600 hover:bg-green-200 p-1 rounded-lg transition-all duration-200 hover:scale-110"
                      title="Edit Admin"
                    >
                      <FaEdit className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleDeactivate(admin._id)}
                      className="bg-red-100 text-red-600 hover:bg-red-200 p-1 rounded-lg transition-all duration-200 hover:scale-110"
                      title="Deactivate Admin"
                    >
                      <FaTrash className="w-3 h-3" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {paginatedAdmins.length === 0 && (
              <tr>
                <td
                  colSpan="5"
                  className="px-8 py-16 text-center"
                >
                  <div className="flex flex-col items-center">
                    <FaUserShield className="w-16 h-16 text-gray-300 mb-4" />
                    <span className="text-gray-500 text-lg font-medium">No admins found</span>
                    <span className="text-gray-400 text-sm mt-2">Start by adding your first admin</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-8 py-6 flex items-center justify-between border-t border-gray-200">
            <div className="bg-white px-3 py-1 rounded-lg shadow-md">
              <p className="text-xs text-gray-700 font-medium">
                Page <span className="font-bold text-blue-600">{currentPage}</span> of{' '}
                <span className="font-bold text-blue-600">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-xl shadow-lg overflow-hidden">
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-xs font-semibold text-gray-500 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-50 transition-all duration-200"
                >
                  Previous
                </button>
                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-xs font-semibold text-gray-500 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-50 transition-all duration-200"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-4xl mx-4 rounded-2xl shadow-2xl p-4 relative border border-gray-200 max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center">
                  <FaUserShield className="text-blue-600 mr-2 text-lg" />
                  {isEditing ? "Edit Admin" : "Add New Admin"}
                </h3>
                <p className="text-gray-600 mt-1 text-xs">
                  {isEditing ? "Update admin information" : "Create a new administrator account"}
                </p>
              </div>
              <button
                onClick={closeFormModal}
                className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-all duration-200"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* username */}
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaUserShield className="w-3 h-3 text-blue-600" />
                    <span>Username</span>
                  </div>
                </label>
                <input
                  type="text"
                  name="username"
                  value={form.username}
                  onChange={handleInputChange}
                  placeholder="Enter admin username"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white shadow-sm hover:shadow-md transition-all duration-200"
                />
              </div>
              {/* email */}
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaEnvelope className="w-3 h-3 text-green-600" />
                    <span>Email</span>
                  </div>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleInputChange}
                  placeholder="Enter email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white shadow-sm hover:shadow-md transition-all duration-200"
                />
              </div>
              {/* password */}
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaTimes className="w-3 h-3 text-red-600" />
                    <span>Password</span>
                  </div>
                </label>
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleInputChange}
                  placeholder="Enter secure password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white shadow-sm hover:shadow-md transition-all duration-200"
                />
              </div>
              {/* phoneNumber */}
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaPhone className="w-3 h-3 text-purple-600" />
                    <span>Phone Number</span>
                  </div>
                </label>
                <input
                  value={form.phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder="Enter phone number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white shadow-sm hover:shadow-md transition-all duration-200"
                />
              </div>
              {/* address */}
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaMapMarkerAlt className="w-3 h-3 text-orange-600" />
                    <span>Address</span>
                  </div>
                </label>
                <input
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleInputChange}
                  placeholder="Enter full address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white shadow-sm hover:shadow-md transition-all duration-200"
                />
              </div>
              {/* companies */}
              <div className="col-span-1 sm:col-span-2 lg:col-span-3 bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaBuilding className="w-3 h-3 text-indigo-600" />
                    <span>Associated Companies</span>
                  </div>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-24 overflow-y-auto bg-white border border-gray-300 p-2 rounded-lg">
                  {companies.map((company) => (
                    <div key={company._id} className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors duration-200">
                      <input
                        type="checkbox"
                        id={`company-${company._id}`}
                        value={company._id}
                        checked={form.companies.includes(company._id)}
                        onChange={handleCompanyChange}
                        className="w-3 h-3 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <label htmlFor={`company-${company._id}`} className="text-xs font-medium text-gray-700 cursor-pointer">
                        {company.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              {/* dateOfBirth */}
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaCalendar className="w-3 h-3 text-pink-600" />
                    <span>Date of Birth</span>
                  </div>
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={form.dateOfBirth}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white shadow-sm hover:shadow-md transition-all duration-200"
                />
              </div>
              {/* dateOfJoining */}
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  <div className="flex items-center space-x-1">
                    <FaCalendar className="w-3 h-3 text-teal-600" />
                    <span>Date of Joining</span>
                  </div>
                </label>
                <input
                  type="date"
                  name="dateOfJoining"
                  value={form.dateOfJoining}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white shadow-sm hover:shadow-md transition-all duration-200"
                />
              </div>

            </div>
            {/* Modal Action Buttons */}
            <div className="flex justify-end space-x-2 pt-3 border-t border-gray-200 mt-3">
              <button
                onClick={closeFormModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 font-semibold text-sm transition-all duration-200 flex items-center space-x-1"
              >
                <FaTimes className="w-3 h-3" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleAddOrUpdateAdmin}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 flex items-center space-x-1 font-semibold text-sm shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                <FaSave className="w-3 h-3" />
                <span>{isEditing ? "Update" : "Create"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-3xl mx-4 rounded-2xl shadow-2xl p-4 relative border border-gray-200 max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white w-12 h-12 rounded-xl flex items-center justify-center font-bold text-sm">
                  {selectedAdmin.username?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <h3 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    {selectedAdmin.username}
                  </h3>
                  <p className="text-gray-600 text-xs">Administrator Details</p>
                </div>
              </div>
              <button
                onClick={closeDetailsModal}
                className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-all duration-200"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Personal Information */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-xl border border-blue-200">
                <div className="flex items-center space-x-1 mb-2">
                  <FaUserShield className="w-3 h-3 text-blue-600" />
                  <h3 className="text-sm font-bold text-gray-800">Personal Information</h3>
                </div>
                <div className="space-y-2">
                  <div className="bg-white p-2 rounded-lg">
                    <div className="flex items-center space-x-1 mb-1">
                      <FaEnvelope className="w-3 h-3 text-blue-600" />
                      <span className="font-semibold text-gray-700 text-xs">Email:</span>
                    </div>
                    <p className="text-gray-900 text-xs ml-4">{selectedAdmin.email}</p>
                  </div>
                  <div className="bg-white p-2 rounded-lg">
                    <div className="flex items-center space-x-1 mb-1">
                      <FaPhone className="w-3 h-3 text-blue-600" />
                      <span className="font-semibold text-gray-700 text-xs">Phone:</span>
                    </div>
                    <p className="text-gray-900 text-xs ml-4">{selectedAdmin.phoneNumber}</p>
                  </div>
                  <div className="bg-white p-2 rounded-lg">
                    <div className="flex items-center space-x-1 mb-1">
                      <FaMapMarkerAlt className="w-3 h-3 text-blue-600" />
                      <span className="font-semibold text-gray-700 text-xs">Address:</span>
                    </div>
                    <p className="text-gray-900 text-xs ml-4">{selectedAdmin.address}</p>
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-3 rounded-xl border border-purple-200">
                <div className="flex items-center space-x-1 mb-2">
                  <FaCalendar className="w-3 h-3 text-purple-600" />
                  <h3 className="text-sm font-bold text-gray-800">Additional Details</h3>
                </div>
                <div className="space-y-2">
                  <div className="bg-white p-2 rounded-lg">
                    <span className="font-semibold text-gray-700 text-xs">Status:</span>
                    <div className="mt-1">
                      <span className={`px-2 py-1 inline-flex items-center space-x-1 text-xs font-bold rounded-full shadow-sm ${
                        selectedAdmin.isActive 
                          ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800' 
                          : 'bg-gradient-to-r from-red-100 to-red-200 text-red-800'
                      }`}>
                        {selectedAdmin.isActive ? <FaCheck className="w-2 h-2" /> : <FaTimes className="w-2 h-2" />}
                        <span>{selectedAdmin.isActive ? 'Active' : 'Inactive'}</span>
                      </span>
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded-lg">
                    <span className="font-semibold text-gray-700 text-xs">Date of Birth:</span>
                    <p className="text-gray-900 text-xs mt-1">
                      {selectedAdmin.dateOfBirth
                        ? new Date(selectedAdmin.dateOfBirth).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })
                        : "Not specified"}
                    </p>
                  </div>
                  <div className="bg-white p-2 rounded-lg">
                    <span className="font-semibold text-gray-700 text-xs">Date of Joining:</span>
                    <p className="text-gray-900 text-xs mt-1">
                      {selectedAdmin.dateOfJoining
                        ? new Date(selectedAdmin.dateOfJoining).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })
                        : "Not specified"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Associated Companies */}
              <div className="lg:col-span-2 bg-gradient-to-br from-green-50 to-green-100 p-3 rounded-xl border border-green-200">
                <div className="flex items-center space-x-1 mb-2">
                  <FaBuilding className="w-3 h-3 text-green-600" />
                  <h3 className="text-sm font-bold text-gray-800">Associated Companies</h3>
                </div>
                <div className="bg-white p-2 rounded-lg">
                  {selectedAdmin.companies && selectedAdmin.companies.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {selectedAdmin.companies.map((company, index) => (
                        <div
                          key={company._id}
                          className="flex items-center space-x-1 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold shadow-sm"
                        >
                          <div className="bg-green-500 text-white w-4 h-4 rounded-full flex items-center justify-center font-bold text-xs">
                            {index + 1}
                          </div>
                          <span>{company.name}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <FaBuilding className="w-6 h-6 text-gray-300 mx-auto mb-1" />
                      <span className="text-gray-500 italic text-xs">No companies assigned</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;